import { useState, useEffect, useCallback, useRef } from "react";
import { api, apiError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { toast } from "sonner";
import { UploadCloud, Loader2, CheckCircle2, HelpCircle, AlertTriangle, Search } from "lucide-react";

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.tif,.tiff";

// Deliberately money-free. A Bill Uploader drops bills and resolves the Cust ID —
// they never see amounts or any other screen.
const STATUS_META = {
  needs_cust_id: { label: "Needs Cust ID", cls: "bg-amber-100 text-amber-800", Icon: HelpCircle },
  orphan: { label: "Couldn't place", cls: "bg-rose-100 text-rose-700", Icon: AlertTriangle },
  review: { label: "Reading / review", cls: "bg-slate-100 text-slate-600", Icon: Loader2 },
  unassigned: { label: "AI reading…", cls: "bg-slate-100 text-slate-500", Icon: Loader2 },
  matched: { label: "Placed", cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  paid: { label: "Placed", cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  duplicate: { label: "Duplicate", cls: "bg-rose-100 text-rose-700", Icon: AlertTriangle },
};

export default function UploadBills() {
  const [rows, setRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef();

  const load = useCallback(async () => {
    try { const { data } = await api.get("/vendor-invoices/my-uploads"); setRows(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not load your uploads"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // poll briefly after an upload so statuses settle from "AI reading…"
  const pollAfterUpload = useCallback(() => {
    let n = 0;
    const t = setInterval(async () => {
      n += 1;
      await load();
      if (n >= 10) clearInterval(t);
    }, 3000);
  }, [load]);

  const doUpload = async (fileList) => {
    const files = Array.from(fileList || []); if (!files.length) return;
    const fd = new FormData(); files.forEach((f) => fd.append("files", f));
    setUploading(true);
    try {
      const { data } = await api.post("/vendor-invoices/upload", fd);
      const c = data.created?.length || 0;
      if (c) toast.success(`${c} bill${c === 1 ? "" : "s"} uploaded — AI reading…`);
      (data.rejected || []).forEach((r) => toast.error(r.reason));
      await load(); pollAfterUpload();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.rejected) d.rejected.forEach((r) => toast.error(r.reason));
      else toast.error(apiError(d) || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <div className="space-y-5 max-w-4xl">
      <div>
        <h1 className="font-head text-2xl font-extrabold">Upload Bills</h1>
        <p className="text-sm text-slate-500 mt-1">Drop vendor bills here. If a bill can't be placed on a link automatically, pick the Nextra Cust ID and we'll match it.</p>
      </div>

      <div
        onDragEnter={() => setDragging(true)}
        onDragLeave={() => setDragging(false)}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); setDragging(false); doUpload(e.dataTransfer.files); }}
        className={`rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${dragging ? "border-blue-500 bg-blue-50" : "border-slate-300 bg-white"}`}>
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-slate-600"><Loader2 className="animate-spin" size={18} /> Uploading…</div>
        ) : (
          <>
            <UploadCloud className="mx-auto text-slate-400" size={28} />
            <div className="mt-2 text-sm text-slate-600">Drag & drop bills here, or <button onClick={() => fileInput.current?.click()} className="text-[var(--brand)] font-semibold underline">choose files</button></div>
            <div className="mt-1 text-xs text-slate-400">PDF, JPG, PNG, TIFF · up to 15 MB each · multiple at once</div>
          </>
        )}
        <input ref={fileInput} type="file" accept={ACCEPT} multiple className="hidden"
          onChange={(e) => { doUpload(e.target.files); e.target.value = ""; }} />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b text-left text-[11px] uppercase text-slate-500">
              <th className="px-4 py-3">Bill</th>
              <th className="px-4 py-3">ISP</th>
              <th className="px-4 py-3">Uploaded</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Cust ID / link</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => <UploadRow key={r.id} row={r} onResolved={load} />)}
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No uploads yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UploadRow({ row, onResolved }) {
  const meta = STATUS_META[row.status] || STATUS_META.review;
  const { Icon } = meta;
  const needs = row.status === "needs_cust_id" || row.status === "orphan";
  return (
    <tr className="border-b border-slate-100 align-top">
      <td className="px-4 py-3 font-medium text-slate-800">{row.original_filename}</td>
      <td className="px-4 py-3 text-slate-600">{row.vendor || "—"}</td>
      <td className="px-4 py-3 text-slate-500">{fmtDate(row.created_at)}</td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${meta.cls}`}>
          <Icon size={12} className={row.status === "unassigned" || row.status === "review" ? "animate-spin" : ""} /> {meta.label}
        </span>
      </td>
      <td className="px-4 py-3">
        {row.matched_customer_name ? (
          <span className="text-slate-700">{row.matched_customer_name} <span className="text-xs text-slate-400">{row.matched_internal_id}</span></span>
        ) : needs ? (
          <CustIdPicker iid={row.id} candidates={row.candidates} onResolved={onResolved} />
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
    </tr>
  );
}

function CustIdPicker({ iid, candidates, onResolved }) {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!q || q.length < 2) { setOpts([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/customers?q=${encodeURIComponent(q)}&limit=8`); setOpts(data); setOpen(true); }
      catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const resolve = async (code) => {
    if (!code) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("cust_id", code);
      const { data } = await api.post(`/vendor-invoices/${iid}/resolve-cust-id`, fd);
      const r = data.resolution;
      toast.success(r === "ill_one" ? "Matched — awaiting confirmation"
        : r === "ill_several" ? "Still a few links fit — Accounts will confirm"
        : r === "orphan" ? "Couldn't place under that customer" : "Updated");
      setOpen(false); setQ(""); onResolved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not resolve"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-2">
      {candidates?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {candidates.map((c) => (
            <button key={c.item_id} disabled={busy} onClick={() => resolve(c.customer_code)}
              className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50">
              {c.customer_name || c.internal_billable_id} {c.bandwidth ? <span className="text-slate-400">· {c.bandwidth}</span> : null}
            </button>
          ))}
        </div>
      )}
      <div className="relative w-64">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find Nextra Cust ID…"
          className="w-full pl-8 pr-3 h-9 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500" />
        {open && opts.length > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
            {opts.map((o) => (
              <button key={o.id} disabled={busy} onClick={() => resolve(o.code || o.name)}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 text-sm">
                <span className="font-medium">{o.code || "—"}</span> <span className="text-slate-500">{o.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
